import { getBackofficeUrl } from 'utils/urls';

export type MaintenanceInfo = {
  title?: string;
  displayMessage?: string;
  endDatetime?: string;
};

/** Upper bound for the pre-entry maintenance check; past it the check fails open. */
export const MAINTENANCE_CHECK_TIMEOUT_MS = 8000;

export type MaintenanceCheck = { active: boolean; info: MaintenanceInfo | null };

/**
 * Pre-entry maintenance gate — Unity parity (AuthenticationModel.CheckForMaintenance
 * → api/maintenance-schedule/get-game-close-maintenance-schedule). Called with the
 * player's access token right after authentication; a non-empty `data` array means
 * the game-close maintenance window is active, so the caller shows a maintenance
 * screen INSTEAD of entering the app (i.e. the player can't log in during
 * maintenance).
 *
 * Fails OPEN: no token, or any network/parse error, resolves to `active: false`
 * so a flaky check never locks players out (mirrors Unity, which proceeds when
 * the request fails).
 */
export async function fetchGameCloseMaintenance(token: string): Promise<MaintenanceCheck> {
  if (!token) return { active: false, info: null };
  // Bounded: this runs while the native login splash is up, so a stalled request
  // would otherwise leave the player on the splash with no way forward.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MAINTENANCE_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(
      `${getBackofficeUrl()}/api/maintenance-schedule/get-game-close-maintenance-schedule`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `token=${encodeURIComponent(token)}`,
        signal: controller.signal
      }
    );
    const json = await res.json();
    const record = Array.isArray(json?.data) ? json.data[0] : null;
    if (!record) return { active: false, info: null };
    return {
      active: true,
      info: {
        title: record.title,
        displayMessage: record.display_message,
        endDatetime: record.end_datetime
      }
    };
  } catch {
    return { active: false, info: null };
  } finally {
    clearTimeout(timer);
  }
}
